/**
 * A TokenStore kept in localStorage (so a till stays signed in across reloads) with
 * an in-memory copy, change notifications for React (`useSyncExternalStore`), and
 * updates from other tabs (`storage` events: another tab refreshed or signed out).
 * Every storage access is guarded: without storage the session lives in memory only.
 */
import type { TokenStore } from './client.js';
import type { TokenPair } from './types.js';

export interface ObservableTokenStore extends TokenStore {
  subscribe(listener: () => void): () => void;
}

function storageOrNull(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function parse(raw: string | null): TokenPair | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<TokenPair> | null;
    return value && typeof value.accessToken === 'string' && typeof value.refreshToken === 'string'
      ? (value as TokenPair)
      : null;
  } catch {
    return null;
  }
}

export function createStorageTokenStore(
  key: string,
  storage: Storage | null = storageOrNull(),
): ObservableTokenStore {
  const listeners = new Set<() => void>();
  let current: TokenPair | null = null;
  try {
    current = parse(storage?.getItem(key) ?? null);
  } catch {
    current = null;
  }
  const notify = () => listeners.forEach((l) => l());

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key !== key) return;
      current = parse(event.newValue);
      notify();
    });
  }

  return {
    get: () => current,
    set(tokens) {
      current = tokens;
      try {
        if (tokens) storage?.setItem(key, JSON.stringify(tokens));
        else storage?.removeItem(key);
      } catch {
        // Storage unavailable or full: keep the session in memory for this tab.
      }
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
